import type { Example } from "../schema/master";
import type { Scenario } from "../schema/scenario";
import { buildScenario, type BuildOptions } from "../engine/scenario-ops";
import { EQUIPMENT_TEMPLATES } from "./templates";
import { TECHNOLOGIES } from "./technologies";

/*
 * 예시 , 경로 조합(생산방식 , 전력원 , 저장,이송 , 산정방식)으로 만든 출발점.
 * 사용자가 홈에서 고르는 조합과 같은 방법으로 조립되므로 값이 두 곳에 있지 않다.
 * 기준결과값(reference)은 전문가 검산 전까지 null , 테스트가 엔진 결과와 대조한다.
 */
const FIXED = { createdAt: "2026-10-01T00:00:00.000Z", updatedAt: "2026-10-01T00:00:00.000Z" };

function build(id: string, name: string, opts: Omit<BuildOptions, "idPrefix" | "baseYear" | "name">): Scenario {
  const tech = TECHNOLOGIES.find((t) => t.id === opts.techId)?.name;
  const s = buildScenario({ ...opts, name, idPrefix: id, baseYear: 2024 }, EQUIPMENT_TEMPLATES, tech);
  return { ...s, exampleId: id, meta: { ...s.meta, ...FIXED } };
}

export const EXAMPLES: Example[] = [
  {
    id: "ex.aec_grid_tube",
    name: "알칼라인 수전해(계통전력) → 압축 → 튜브트레일러 → 충전소",
    description: "기본 통합 예시. 계통전력 100% 로 수전해한 수소를 200 bar 로 압축해 200 km 튜브트레일러로 운송하고 충전소에서 재압축,저장한다. 전력 배출계수가 결과의 90% 이상을 차지한다.",
    tags: ["기본", "수전해", "계통전력", "압축", "튜브트레일러"],
    scenario: build("ex.aec_grid_tube", "알칼라인 수전해(계통) , 튜브트레일러 200 km", { techId: "aec", routeId: "compressed_trailer", electricityPresetId: "grid", purpose: "계통전력 수전해 수소의 충전소 인도 기준 온실가스 배출량 산정" }),
    reference: null, order: 1, active: true,
  },
  {
    id: "ex.aec_renewable_tube",
    name: "알칼라인 수전해(재생에너지 100%) → 압축 → 튜브트레일러 → 충전소",
    description: "같은 공급경로에서 전력원만 재생에너지(단순화 계수 0)로 바꾼 예시. 전력 배출이 사라지고 운송 경유가 남는다. 인증제 모의산정으로 전환하면 1등급 조건을 확인할 수 있다.",
    tags: ["수전해", "재생에너지", "인증제 비교"],
    scenario: build("ex.aec_renewable_tube", "알칼라인 수전해(재생 100%) , 튜브트레일러 200 km", { techId: "aec", routeId: "compressed_trailer", electricityPresetId: "renewable", purpose: "재생에너지 수전해 수소의 청정수소 인증 등급 모의 확인" }),
    reference: null, order: 2, active: true,
  },
  {
    id: "ex.pem_mix_pipeline",
    name: "PEM 수전해(계통 50% + 태양광 50%) → 파이프라인 50 km",
    description: "전력 믹스와 파이프라인 수송을 보는 예시. 전력원 비율을 바꾸며 배출량 변화를 바로 확인한다.",
    tags: ["PEM", "전력 믹스", "파이프라인"],
    scenario: build("ex.pem_mix_pipeline", "PEM 수전해(계통 50%,태양광 50%) , 파이프라인 50 km", { techId: "pem", routeId: "pipeline", electricityPresetId: "mix50", purpose: "전력 믹스에 따른 수전해 수소 배출량 민감도 확인", purityPct: 99.99 }),
    reference: null, order: 3, active: true,
  },
  {
    id: "ex.smr_tube",
    name: "천연가스 개질(SMR) → 압축 → 튜브트레일러 → 충전소",
    description: "개질 수소의 예시. 천연가스 연소와 공급망(상류) 배출이 주요 배출원이며, 인증제 모의산정에서 4 kgCO2eq/kg 기준을 넘는 것을 확인한다.",
    tags: ["개질", "천연가스", "튜브트레일러"],
    scenario: build("ex.smr_tube", "천연가스 개질(SMR) , 튜브트레일러 200 km", { techId: "smr", routeId: "compressed_trailer", purpose: "개질 수소의 충전소 인도 기준 온실가스 배출량 산정" }),
    reference: null, order: 4, active: true,
  },
  {
    id: "ex.byproduct_tube",
    name: "부생수소(PSA 정제, 배분 없음) → 압축 → 튜브트레일러 → 충전소",
    description: "부생수소 예시. 주공정 배출을 수소에 배분하지 않는 기본 정책을 적용했으며, 시나리오 설정에서 배분 정책을 바꿔 결과 변화를 볼 수 있다.",
    tags: ["부생수소", "배분"],
    scenario: build("ex.byproduct_tube", "부생수소(PSA) , 튜브트레일러 200 km", { techId: "byproduct", routeId: "compressed_trailer", purpose: "부생수소의 배분 정책에 따른 배출량 비교" }),
    reference: null, order: 5, active: true,
  },
  {
    id: "ex.aec_liquid_tanker",
    name: "알칼라인 수전해(계통전력) → 액화 → 탱크로리 → 액화저장",
    description: "액화 경로 예시. 액화기 전력(약 10 kWh/kg)과 증발 손실이 압축 경로와 어떻게 다른지 비교한다.",
    tags: ["수전해", "액화", "탱크로리"],
    scenario: build("ex.aec_liquid_tanker", "알칼라인 수전해(계통) , 액화 , 탱크로리 200 km", { techId: "aec", routeId: "liquid_tanker", electricityPresetId: "grid", purpose: "액화수소 공급경로의 온실가스 배출량 산정", purityPct: 99.999 }),
    reference: null, order: 6, active: true,
  },
];
